import { Link, useLocation, useNavigate } from "react-router-dom";
import React, { useState } from "react";
import { confirmSignUp } from "../../services/auth/auth";
import {
  Alert,
  Box,
  Button,
  InputAdornment,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { CheckCircle } from "@mui/icons-material";
import logo from "../../assets/logo-cloud.png";
import { CheckCheck } from "lucide-react";

const ConfirmUserPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || "");
  const [confirmationCode, setConfirmationCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setFormError("");
    setSuccess(false);

    if (!confirmationCode.trim()) {
      setFormError("Please enter the confirmation code");
      return;
    }
    try {
      await confirmSignUp(email, confirmationCode);
      setSuccess(true);
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (error) {
      const errorMessage =
        error.message || "Failed to confirm account. Please try again.";
      setError(errorMessage);
    }
  };
  const handleCloseError = () => {
    setError("");
  };
  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        alignItems: "center",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        backgroundColor: "#003366",
        fontFamily: `"Source Sans Pro", "Amazon Ember", "Raleway", sans-serif`,
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          boxShadow: "inset 0 0 50px rgba(0, 0, 0, 0.5)",
          pointerEvents: "none",
        },
      }}
    >
      <Box
        component="img"
        src={logo}
        alt="Logo"
        sx={{
          position: "absolute",
          top: 20,
          left: 20,
          width: "100px",
        }}
      />
      <Paper
        sx={{
          width: "400px",
          padding: "30px 40px",
          borderRadius: "8px",
          backgroundColor: "rgba(255, 255, 255, 0.85)",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
          textAlign: "center",
        }}
      >
        <Typography
          variant="h5"
          gutterBottom
          sx={{
            color: "#003366",
            fontWeight: 600,
            fontFamily: `"Raleway", sans-serif`,
          }}
        >
          <CheckCircle sx={{ color: "#003366", marginRight: 1 }} />
          Account Confirmation
        </Typography>
        <Typography sx={{ color: "#003366" }}>
          Please confirm your account by entering the confirmation code sent to{" "}
          <b>{email}</b>.
        </Typography>
        <form onSubmit={handleSubmit} className="flex flex-col">
          <TextField
            id="Confirmation"
            label="Confirmation Code"
            type="text"
            value={confirmationCode}
            onChange={(e) => setConfirmationCode(e.target.value)}
            aria-label="Confirmation Code"
            required
            margin="normal"
            error={!!formError}
            helperText={formError}
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CheckCheck className="h-6 w-6 text-[#003366]" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#003366",
                },
              },
              backgroundColor: "#F0F8FF",
              borderRadius: 1,
            }}
          />
          <Box sx={{ textAlign: "center", mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              sx={{
                backgroundColor: "#FFD700",
                color: "#003366",
                padding: "10px 16px",
                borderRadius: "6px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "#FFC300",
                },
              }}
            >
              <Typography
                sx={{ color: "#003366", fontFamily: ` "Raleway", sans-serif` }}
              >
                Confirm Account
              </Typography>
            </Button>
          </Box>
        </form>
        <Box sx={{ mt: 2 }}>
          <Link to="/login">
            <Typography
              sx={{
                marginTop: "20px",
                color: "#003366",
                fontFamily: ` "Raleway", sans-serif`,
              }}
            >
              Already have an account? Log in
            </Typography>
          </Link>
        </Box>
      </Paper>
      {/* Success Message */}
      <Snackbar
        open={success}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="success" sx={{ width: "100%" }}>
          Account confirmed successfully! Redirecting to login...
        </Alert>
      </Snackbar>

      {/* Error Message */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          sx={{ width: "100%" }}
        >
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ConfirmUserPage;
